import { authIdentity } from "@defuse-protocol/internal-utils"
import { QuoteRequest } from "@defuse-protocol/one-click-sdk-typescript"
import { getAuroraEngineContractId } from "@src/components/DefuseSDK/constants/aurora"
import type { SupportedChainName } from "@src/components/DefuseSDK/types/base"
import { isAuroraVirtualChain } from "@src/components/DefuseSDK/utils/blockchain"
import type { ParsedNearcomAddress } from "@src/shared/utils/nearcomAddress"
import { isNearIntentsNetwork } from "@src/shared/utils/networks"

export type ParsedWithdrawRecipient =
  | { kind: "nearcom"; address: ParsedNearcomAddress }
  | { kind: "destination"; address: string }

export type WithdrawQuoteRecipient =
  | { kind: "nearcom"; address: ParsedNearcomAddress }
  | {
      kind: "destination"
      displayAddress: string
      protocolAddress: string
    }

export function resolveWithdrawQuoteRecipient(params: {
  parsedRecipient: ParsedWithdrawRecipient
  displayAddress: string
  blockchain: SupportedChainName | "near_intents"
  destinationChainName: SupportedChainName
}): {
  recipient: WithdrawQuoteRecipient
  destinationChainName: SupportedChainName
  isAurora: boolean
} {
  const {
    parsedRecipient,
    displayAddress,
    blockchain,
    destinationChainName,
  } = params
  const isAurora =
    !isNearIntentsNetwork(blockchain) &&
    isAuroraVirtualChain(destinationChainName)
  const recipient: WithdrawQuoteRecipient =
    parsedRecipient.kind === "nearcom"
      ? { kind: "nearcom", address: parsedRecipient.address }
      : {
          kind: "destination",
          displayAddress,
          protocolAddress: isAurora
            ? getAuroraEngineContractId(destinationChainName)
            : parsedRecipient.address,
        }

  return { recipient, destinationChainName, isAurora }
}

export function parsedWithdrawRecipientsEqual(
  left: ParsedWithdrawRecipient | null,
  right: ParsedWithdrawRecipient | null
): boolean {
  if (left == null || right == null) return left === right
  if (left.kind !== right.kind) return false
  if (left.kind === "nearcom" && right.kind === "nearcom") {
    return left.address.bareIdentifier === right.address.bareIdentifier
  }
  if (left.kind === "destination" && right.kind === "destination") {
    return left.address === right.address
  }
  return false
}

export function parsedWithdrawRecipientMatchesNetwork(
  recipient: ParsedWithdrawRecipient,
  isInternalNetwork: boolean
): boolean {
  return isInternalNetwork
    ? recipient.kind === "nearcom"
    : recipient.kind === "destination"
}

export function withdrawQuoteRecipientsEqual(
  left: WithdrawQuoteRecipient,
  right: WithdrawQuoteRecipient
): boolean {
  if (left.kind !== right.kind) return false
  if (left.kind === "nearcom" && right.kind === "nearcom") {
    return left.address.bareIdentifier === right.address.bareIdentifier
  }
  if (left.kind === "destination" && right.kind === "destination") {
    return (
      left.displayAddress === right.displayAddress &&
      left.protocolAddress === right.protocolAddress
    )
  }
  return false
}

export function serializeWithdrawQuoteRecipient(
  recipient: WithdrawQuoteRecipient,
  isConfidential?: boolean
): {
  displayRecipient: string
  recipient: string
  recipientType: QuoteRequest.recipientType
} {
  if (recipient.kind === "nearcom") {
    return {
      displayRecipient: recipient.address.displayAddress,
      recipient: authIdentity.authHandleToIntentsUserId(
        recipient.address.bareIdentifier,
        "near"
      ),
      recipientType: isConfidential
        ? QuoteRequest.recipientType.CONFIDENTIAL_INTENTS
        : QuoteRequest.recipientType.INTENTS,
    }
  }

  return {
    displayRecipient: recipient.displayAddress,
    recipient: recipient.protocolAddress,
    recipientType: QuoteRequest.recipientType.DESTINATION_CHAIN,
  }
}

export function getWithdrawQuoteRecipientDisplayAddress(
  recipient: WithdrawQuoteRecipient
): string {
  return recipient.kind === "nearcom"
    ? recipient.address.displayAddress
    : recipient.displayAddress
}

export function getParsedWithdrawRecipientAddress(
  recipient: ParsedWithdrawRecipient
): string {
  return recipient.kind === "nearcom"
    ? recipient.address.bareIdentifier
    : recipient.address
}
